import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import { navigationLinks } from '../content/landingContent';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const goHome = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-stone-200`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <img 
              src="/Practicio_logo.png" 
              alt="Practicio" 
              className="h-8 cursor-pointer hover:opacity-80 transition-opacity duration-200"
              onClick={goHome}
            />
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigationLinks.map((link, index) => (
              <button 
                key={index}
                onClick={() => scrollToSection(link.href)}
                className="text-stone-600 hover:text-stone-900 text-sm font-medium transition-colors duration-200"
              >
                {link.label}
              </button>
            ))}
          </nav>
          
          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center space-x-4">
            <a href="https://app.practicio.io/" data-testid="sign-in-link">
              <Button 
                variant="ghost" 
                className="text-stone-700 hover:text-stone-900 hover:bg-stone-100 transition-all duration-200"
              >
                Sign In
              </Button>
            </a>
            <Button 
              className="bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-all duration-300"
              onClick={() => scrollToSection('contact')}
              data-testid="header-book-demo"
            >
              Book a Demo
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-stone-700 hover:text-stone-900 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone-200 animate-in slide-in-from-top duration-200">
            <nav className="flex flex-col space-y-4">
              {navigationLinks.map((link, index) => (
                <button 
                  key={index}
                  onClick={() => scrollToSection(link.href)}
                  className="text-stone-600 hover:text-stone-900 text-sm font-medium transition-colors duration-200 text-left"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-4 space-y-2">
                <a href="https://app.practicio.io/" data-testid="sign-in-link-mobile">
                  <Button 
                    variant="ghost" 
                    className="w-full text-stone-700 hover:text-stone-900 hover:bg-stone-100"
                  >
                    Sign In
                  </Button>
                </a>
                <Button 
                  className="w-full bg-slate-600 hover:bg-slate-700 text-white rounded-lg"
                  onClick={() => scrollToSection('contact')}
                  data-testid="header-book-demo-mobile"
                >
                  Book a Demo
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;